import { Button, IconButton, Tag } from '@ads/components-react';

import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExamItemAnswerTextCorrectionGridItemRepositoryResponse } from '@/shared/api/generated/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Skeleton,
} from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';
import { QuestionAiBatchAction } from '../../molecules/QuestionAiBatchAction';
import { StudentSubTable } from '../StudentSubTable';

// ─── Layout ───────────────────────────────────────────────────────────────────

// Mobile: chevron + enunciado + em aberto | Desktop: + peso
const COLS = 'grid-cols-[2rem_1fr_6rem] md:grid-cols-[2rem_1fr_2rem_6rem]';
// Grade da linha inteira — as mesmas colunas de COLS mais a coluna de ações (botão de IA em
// lote), que fica fora do AccordionTrigger. Cabeçalho e linhas usam esta mesma grade para que
// as colunas alinhem; o AccordionTrigger usa COLS internamente, ocupando todas as colunas desta
// grade exceto a última (`col-span-3`/`md:col-span-4`).
const ROW_COLS = 'grid-cols-[2rem_1fr_6rem_11rem] md:grid-cols-[2rem_1fr_3rem_6rem_6rem]';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface ExamCorrectionTableProps {
  readonly items: ExamItemAnswerTextCorrectionGridItemRepositoryResponse[];
  readonly isLoading: boolean;
  /** ID da publicação do exame — repassado para a sub-tabela de alunos */
  readonly examPublishId: number;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalCount: number;
  readonly onPageChange: (page: number) => void;
  /**
   * Callback ao clicar em "Corrigir" ou "Visualizar" em um aluno da sub-tabela.
   * Recebe o item de questão, o ID da resposta (answerId) do aluno selecionado e a
   * posição absoluta dessa resposta na fila da questão (1-based).
   */
  readonly onCorrect: (
    item: ExamItemAnswerTextCorrectionGridItemRepositoryResponse,
    answerId: number,
    position: number,
  ) => void;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ExamCorrectionSkeleton() {
  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border p-xxxs">
      <div className={`grid ${ROW_COLS} gap-sm px-mini py-micro`}>
        <div />
        <Skeleton className="h-xxs w-[6rem]" />
        <Skeleton className="hidden h-xxs w-sm md:block" />
        <Skeleton className="h-xxs w-xl" />
        <Skeleton className="h-xxs w-xxl" />
      </div>
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className={`grid ${ROW_COLS} items-center gap-sm border-t-hairline border-ctx-layout-border px-mini py-xxs`}
        >
          <Skeleton shape="circle" className="size-mini" />
          <Skeleton className="h-mini w-3/4" />
          <Skeleton className="hidden h-mini w-sm md:block" />
          <Skeleton className="h-sm w-md rounded-pill" />
          <Skeleton className="h-sm w-xxxl rounded-sm" />
        </div>
      ))}
    </div>
  );
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Organism ExamCorrectionTable — tabela de questões com accordion de alunos.
 *
 * Cada linha representa uma questão da avaliação. Ao expandir, carrega a
 * sub-tabela de alunos com seus status de correção (corrigido/pendente).
 * O botão "Corrigir" em cada aluno abre o modal de correção diretamente
 * naquele aluno.
 *
 * @example
 * ```tsx
 * <ExamCorrectionTable
 *   items={questions}
 *   isLoading={isLoading}
 *   examPublishId={examId}
 *   currentPage={page}
 *   totalPages={totalPages}
 *   totalCount={totalCount}
 *   onPageChange={setPage}
 *   onCorrect={(item, answerId, position) => openModal(item, answerId, position)}
 * />
 * ```
 */
export function ExamCorrectionTable({
  items,
  isLoading,
  examPublishId,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  onCorrect,
}: ExamCorrectionTableProps) {
  const { t } = useTranslation('examCorrection');

  const [openItemIds, setOpenItemIds] = useState<Set<number>>(new Set());

  function handleValueChange(openValues: string[]) {
    setOpenItemIds(new Set(openValues.map(Number)));
  }

  // Ao concluir a correção em lote por IA, abre o accordion da questão automaticamente
  // — o operador vê as sugestões (tags "Sugestão da IA") na sub-tabela sem precisar
  // expandir manualmente.
  function handleBatchSuccess(itemId: number) {
    setOpenItemIds(prev => new Set(prev).add(itemId));
  }

  if (isLoading) {
    return <ExamCorrectionSkeleton />;
  }

  if (items.length === 0) {
    return (
      <div className="rounded-sm border-hairline border-ctx-layout-border p-xxxs">
        <EmptyState
          title={t('correction.table.empty')}
          description={t('correction.table.emptyDescription')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-sm">
      <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs">
        {/* Header principal — mesma grade das linhas (ROW_COLS) para que as colunas alinhem
            com o corpo, inclusive a coluna de ações onde fica o botão de IA em lote. */}
        <div className={`grid ${ROW_COLS} gap-xxs text-center px-mini py-micro`}>
          <div />
          <span className="text-ctx-content-title ts-subtitle-xxs">
            {t('correction.table.columns.label')}
          </span>
          <span className="hidden text-ctx-content-title ts-subtitle-xxs md:block">
            {t('correction.table.columns.weight')}
          </span>
          <span className="text-ctx-content-title ts-subtitle-xxs">
            {t('correction.table.columns.pending')}
          </span>
          <span className="text-ctx-content-title ts-subtitle-xxs">
            {t('correction.table.columns.actions')}
          </span>
        </div>

        <Accordion
          type="multiple"
          value={Array.from(openItemIds, String)}
          onValueChange={handleValueChange}
        >
          {items.map(item => {
            const itemId = item.id ?? 0;
            const isOpen = openItemIds.has(itemId);
            const pendingCount = item.correctionPendingCount ?? 0;

            return (
              <AccordionItem
                key={itemId}
                value={String(itemId)}
                className="border-t-hairline border-t-ctx-layout-border"
              >
                {/* O botão de correção em lote vive fora do AccordionTrigger — nunca um
                    <button> aninhado em outro. A grade é a mesma do cabeçalho (ROW_COLS); o
                    AccordionTrigger ocupa todas as colunas exceto a última (ações), com sua
                    própria grade interna (COLS) — os tamanhos das trilhas são idênticos aos
                    correspondentes em ROW_COLS, então as colunas alinham entre si. */}
                <div
                  className={[
                    `grid ${ROW_COLS} h-[4.5rem] items-center gap-xxs px-mini border-ctx-layout-border transition-colors`,
                    isOpen
                      ? 'bg-ctx-interactive-secondary border-b-hairline hover:bg-ctx-interactive-secondaryHover'
                      : 'hover:bg-ctx-layout-body',
                  ].join(' ')}
                >
                  <AccordionTrigger
                    className={`col-span-3 grid ${COLS} h-full content-center items-center gap-sm md:col-span-4`}
                  >
                    <IconButton
                      ariaLabel=""
                      hierarchy="secondary"
                      size="sm"
                      icon={ChevronRightIcon}
                      className="group-data-[state=open]:rotate-90"
                    />

                    <p className="line-clamp-2 text-left ts-paragraph-xxs text-ctx-content-base">
                      {item.labelClean ?? '—'}
                    </p>

                    <span className="hidden text-right ts-paragraph-xxs text-ctx-content-base md:block">
                      {item.weight ?? 0}
                    </span>

                    <div className="flex justify-center">
                      {pendingCount > 0 ? (
                        <Tag
                          status="helper"
                          label={String(pendingCount)}
                          custom={{ text: 'text-nowrap' }}
                        />
                      ) : (
                        <Tag
                          status="positive"
                          label={t('correction.allCorrected')}
                          custom={{ text: 'text-nowrap' }}
                        />
                      )}
                    </div>
                  </AccordionTrigger>

                  <div className="flex items-center justify-center">
                    <QuestionAiBatchAction
                      examPublishId={examPublishId}
                      examItemId={itemId}
                      questionLabel={item.label}
                      pendingCount={pendingCount}
                      onBatchSuccess={() => handleBatchSuccess(itemId)}
                    />
                  </div>
                </div>

                <AccordionContent className="bg-ctx-layout-surface">
                  <StudentSubTable
                    examPublishId={examPublishId}
                    examItemId={itemId}
                    enabled={isOpen}
                    onCorrectStudent={(answerId, position) => onCorrect(item, answerId, position)}
                  />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:justify-between">
          <span className="text-ctx-content-placeholder ts-caption-xxs">
            {t('correction.pagination.showing', {
              count: items.length,
              total: totalCount,
            })}
          </span>
          <div className="flex items-center gap-nano">
            <Button
              hierarchy="secondary"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              {t('dashboard.pagination.previous')}
            </Button>
            <span className="text-ctx-content-base ts-caption-xxs">
              {currentPage} / {totalPages}
            </span>
            <Button
              hierarchy="secondary"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              {t('dashboard.pagination.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
